<template>
  <div @drop.prevent="addDropFile" @dragover.prevent>
    <v-expand-transition>
      <AppBadge :model-value="!!url" color="success" icon="mdi:check-bold" class="mb-3" end>
        <embed v-if="file && isPDF(file)" :src />
        <template v-else-if="src">
          <embed v-if="invalidImgSrc" :src />
          <AppImg v-else :src @error="invalidImgSrc = true" />
        </template>
        <LightBox v-else class="placeholder" @click="inputRef?.click">
          <AppIcon icon="mdi:image-plus" color="primary" size="x-large" />
          {{ $tt('click_drag_image') }}
        </LightBox>
      </AppBadge>
    </v-expand-transition>

    <AppBtn v-if="url" size="small" rounded @click="inputRef?.click">
      <AppIcon icon="mdi:restart" start />
      {{ $tt('change_image') }}
    </AppBtn>

    <v-file-input
      v-model="file"
      :label :hint :loading
      :rules="url ? [] : [required, imageTypeRule, fileSizeRule]"
      :accept="Object.values(IMAGE_MIME_TYPES).join(',')"
      :class="{ hidden: url }"
      variant="underlined"
      density="compact"
      loader-height="5"
      truncate-length="30"
      ref="inputRef"
      chips persistent-hint
    />
  </div>
</template>

<script setup lang="ts">
import type { VFileInput } from 'vuetify/components';
import { isPDF, IMAGE_MIME_TYPES } from '~/helpers/media';

const emit = defineEmits<{
  change: [File | null];
}>();

const props = defineProps<{
  url?: string;
  loading: boolean;
  label: string;
  hint: string;
}>();

const { imageTypeRule, fileSizeRule, required } = useValidation();

const file = ref<File | null>(null);
const inputRef = useTemplateRef<VFileInput>('inputRef');
const invalidImgSrc = ref(false);

const src = computed(() => file.value ? URL.createObjectURL(file.value) : props.url);

watch(file, file => emit('change', file));

function addDropFile(e: DragEvent) {
  const { files } = e.dataTransfer!;
  if (!files.length) return;
  file.value = files[0];
}
</script>

<style lang="scss" scoped>
$height: 280px;
img {
  max-height: $height;
  object-fit: contain;
}
embed {
  height: $height;
  width: 100%;
}
.placeholder {
  transition: background-color .15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  margin: 0 auto;

  &:hover {
    background-color: #f3f3f3;
  }
}
:deep(.v-badge__wrapper) {
  justify-content: center;
}
</style>